import { Box, Typography } from '@mui/material';

import { FORTA_ERROR } from '../../../../../../../../../utils/colors';

import type {
  IParencetageMasteryCriteriaItem,
  ISAGMasteryCriteria,
  ITreatmentPlanGoal,
} from '../../../../../../../../../utils/types';
import type { IMigrationPreviewState } from '../../types/states';

interface IMigrationModalContentProps {
  goal: ITreatmentPlanGoal;
  migrationPreviewState: IMigrationPreviewState | null;
}

function isSAGMasteryCriteria(
  criteria: ITreatmentPlanGoal['mastery_criteria'],
): criteria is ISAGMasteryCriteria {
  return Boolean(
    criteria &&
      'acquisition' in criteria &&
      'maintenance_frequency' in criteria &&
      'total_attempts' in criteria.acquisition,
  );
}

function isPercentageMasteryCriteria(
  criteria: ITreatmentPlanGoal['mastery_criteria'],
): criteria is IParencetageMasteryCriteriaItem {
  return Boolean(criteria && 'desired_amount' in criteria && 'total_attempts' in criteria);
}

export function MigrationModalSAGV2GoalContent({
  goal,
  migrationPreviewState,
}: IMigrationModalContentProps) {
  const currentCriteria = isSAGMasteryCriteria(goal.mastery_criteria)
    ? goal.mastery_criteria
    : null;

  return migrationPreviewState ? (
    <Box py={2} data-testid="baselineVersionMigrationSAGV2Content">
      <Typography fontWeight={500}>
        This action may update the Baseline and Mastering criteria. Please review the data and make
        any necessary adjustment before concluding the treatment plan.
      </Typography>
      <Typography color={FORTA_ERROR} my={1} fontSize={14}>
        Calculations are based on saved data. If you have unsaved changes, please save them first.
      </Typography>
      <Box my={2}>
        <Typography fontSize={18} fontWeight={700}>
          Baseline
        </Typography>
        <Box p={2}>
          <Box>
            <Typography display="inline" fontWeight={600}>
              Current:{' '}
            </Typography>
            <Typography display="inline" fontWeight={700}>
              {goal?.baseline_at_creation ?? '__'}
            </Typography>
          </Box>
          <Box>
            <Typography display="inline" fontWeight={600}>
              New:{' '}
            </Typography>
            <Typography display="inline" fontWeight={700}>
              {migrationPreviewState?.baseline_at_creation ?? '__'}
            </Typography>
            <Typography display="inline"> out of </Typography>
            <Typography display="inline" fontWeight={700}>
              {migrationPreviewState?.measurement_units?.total_attempts ?? '__'}
            </Typography>
            <Typography display="inline"> opportunities.</Typography>
          </Box>
        </Box>
      </Box>
      <Box my={2}>
        <Typography fontSize={18} fontWeight={700}>
          Mastering criteria
        </Typography>
        <Box px={2} fontWeight={500} mt={2}>
          <Typography fontWeight={600}>Acquisition</Typography>
          <Box mt={1}>
            <Typography display="inline" fontWeight={600}>
              Current:
            </Typography>
            <Typography display="inline" fontWeight={700} ml={0.5}>
              {currentCriteria?.acquisition?.desired_amount ?? '__'}
            </Typography>
            <Typography display="inline"> out of </Typography>
            <Typography display="inline" fontWeight={700}>
              {currentCriteria?.acquisition?.total_attempts ?? '__'}
            </Typography>
            <Typography display="inline"> opportunities over </Typography>
            <Typography display="inline" fontWeight={700}>
              {currentCriteria?.acquisition?.practice_duration_count ?? '__'}
            </Typography>
            <Typography display="inline"> consecutive sessions.</Typography>
          </Box>
          <Box mt={0.5}>
            <Typography display="inline" fontWeight={600}>
              New:{' '}
            </Typography>
            <Typography display="inline">Client will correctly respond in</Typography>
            <Typography display="inline" fontWeight={700} ml={0.5}>
              {migrationPreviewState?.mastery_criteria?.acquisition?.desired_amount ?? '__'}
            </Typography>
            <Typography display="inline"> out of </Typography>
            <Typography display="inline" fontWeight={700}>
              {migrationPreviewState?.measurement_units?.total_attempts ?? '__'}
            </Typography>
            <Typography display="inline"> opportunities over </Typography>
            <Typography display="inline" fontWeight={700}>
              {migrationPreviewState?.mastery_criteria?.acquisition?.practice_duration_count ??
                '__'}
            </Typography>
            <Typography display="inline"> consecutive sessions.</Typography>
          </Box>
        </Box>
        <Box px={2} fontWeight={500} mt={2}>
          <Typography fontWeight={600}>Generalization</Typography>
          <Box mt={1}>
            <Typography display="inline" fontWeight={600}>
              Current:{' '}
            </Typography>
            <Typography display="inline" fontWeight={700} ml={0.5}>
              {currentCriteria?.generalization?.desired_amount ?? '__'}
            </Typography>
            <Typography display="inline"> out of </Typography>
            <Typography display="inline" fontWeight={700}>
              {currentCriteria?.generalization?.total_attempts ?? '__'}
            </Typography>
            <Typography display="inline"> opportunities over </Typography>
            <Typography display="inline" fontWeight={700}>
              {currentCriteria?.generalization?.practice_duration_count ?? '__'}
            </Typography>
            <Typography display="inline"> consecutive sessions.</Typography>
          </Box>
          <Box mt={0.5}>
            <Typography display="inline" fontWeight={600}>
              New:{' '}
            </Typography>
            <Typography display="inline">Client will correctly respond in</Typography>
            <Typography display="inline" fontWeight={700} ml={0.5}>
              {migrationPreviewState?.mastery_criteria?.generalization?.desired_amount ?? '__'}
            </Typography>
            <Typography display="inline"> out of </Typography>
            <Typography display="inline" fontWeight={700}>
              {migrationPreviewState?.measurement_units?.total_attempts ?? '__'}
            </Typography>
            <Typography display="inline"> opportunities over </Typography>
            <Typography display="inline" fontWeight={700}>
              {migrationPreviewState?.mastery_criteria?.generalization?.practice_duration_count ??
                '__'}
            </Typography>
            <Typography display="inline"> consecutive sessions.</Typography>
          </Box>
        </Box>
        <Box px={2} fontWeight={500} mt={2}>
          <Typography fontWeight={600}>Maintenance</Typography>
          <Box mt={1}>
            <Typography display="inline" fontWeight={600}>
              Current:{' '}
            </Typography>
            <Typography display="inline" fontWeight={700} ml={0.5}>
              {currentCriteria?.maintenance?.desired_amount ?? '__'}
            </Typography>
            <Typography display="inline"> out of </Typography>
            <Typography display="inline" fontWeight={700}>
              {currentCriteria?.maintenance?.total_attempts ?? '__'}
            </Typography>
            <Typography display="inline"> opportunities over </Typography>
            <Typography display="inline" fontWeight={700}>
              {currentCriteria?.maintenance?.practice_duration_count ?? '__'}
            </Typography>
            <Typography display="inline"> consecutive sessions.</Typography>
          </Box>
          <Box mt={0.5}>
            <Typography display="inline" fontWeight={600}>
              New:{' '}
            </Typography>
            <Typography display="inline">Client will correctly respond in</Typography>
            <Typography display="inline" fontWeight={700} ml={0.5}>
              {migrationPreviewState?.mastery_criteria?.maintenance?.desired_amount ?? '__'}
            </Typography>
            <Typography display="inline"> out of </Typography>
            <Typography display="inline" fontWeight={700}>
              {migrationPreviewState?.measurement_units?.total_attempts ?? '__'}
            </Typography>
            <Typography display="inline"> opportunities over </Typography>
            <Typography display="inline" fontWeight={700}>
              {migrationPreviewState?.mastery_criteria?.maintenance?.practice_duration_count ??
                '__'}
            </Typography>
            <Typography display="inline"> consecutive sessions.</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  ) : null;
}

export function MigrationModalSAGV1GoalContent({
  goal,
  migrationPreviewState,
}: IMigrationModalContentProps) {
  const currentCriteria = isPercentageMasteryCriteria(goal.mastery_criteria)
    ? goal.mastery_criteria
    : null;

  return migrationPreviewState ? (
    <Box py={2} data-testid="baselineVersionMigrationSAGV1Content">
      <Typography fontWeight={500}>
        This action may update the Baseline and Mastering criteria. Please review the data and make
        any necessary adjustment before concluding the treatment plan.
      </Typography>
      <Typography color={FORTA_ERROR} my={2} fontSize={14}>
        Calculations are based on saved data. If you have unsaved changes, please save them first.
      </Typography>
      <Box my={2}>
        <Typography fontSize={18} fontWeight={700}>
          Baseline
        </Typography>
        <Box p={2}>
          <Box>
            <Typography display="inline" fontWeight={600}>
              Current:{' '}
            </Typography>
            <Typography display="inline" fontWeight={700}>
              {goal?.baseline_at_creation ?? '__'}
            </Typography>
          </Box>
          <Box mt={0.5}>
            <Typography display="inline" fontWeight={600}>
              New:{' '}
            </Typography>
            <Typography display="inline" fontWeight={700}>
              {migrationPreviewState?.baseline_at_creation ?? '__'}
            </Typography>
            <Typography display="inline"> out of </Typography>
            <Typography display="inline" fontWeight={700}>
              {migrationPreviewState?.measurement_units?.total_attempts ?? '__'}
            </Typography>
            <Typography display="inline"> opportunities.</Typography>
          </Box>
        </Box>
      </Box>
      <Box my={2}>
        <Typography fontSize={18} fontWeight={700}>
          Mastering criteria
        </Typography>
        <Box px={2} fontWeight={500} mt={2}>
          <Typography display="inline" fontWeight={600}>
            Current:{' '}
          </Typography>
          <Typography display="inline" fontWeight={700} ml={0.5}>
            {currentCriteria?.desired_amount ?? '__'}
          </Typography>
          <Typography display="inline"> out of </Typography>
          <Typography display="inline" fontWeight={700}>
            {currentCriteria?.total_attempts ?? '__'}
          </Typography>
          <Typography display="inline" mx={0.5}>
            opportunities observed by BCBA, or self reported by parent during Parent Training over
          </Typography>
          <Typography display="inline" fontWeight={700}>
            {currentCriteria?.practice_duration_count ?? '__'}
          </Typography>
          <Typography display="inline"> consecutive sessions.</Typography>
        </Box>
        <Box px={2} fontWeight={500} mt={0.5}>
          <Typography display="inline" fontWeight={600}>
            New:{' '}
          </Typography>
          <Typography display="inline">Client will correctly respond in</Typography>
          <Typography display="inline" fontWeight={700} ml={0.5}>
            {migrationPreviewState?.mastery_criteria?.desired_amount ?? '__'}
          </Typography>
          <Typography display="inline"> out of </Typography>
          <Typography display="inline" fontWeight={700}>
            {migrationPreviewState?.measurement_units?.total_attempts ?? '__'}
          </Typography>
          <Typography display="inline"> opportunities over </Typography>
          <Typography display="inline" fontWeight={700}>
            {migrationPreviewState?.mastery_criteria?.practice_duration_count ?? '__'}
          </Typography>
          <Typography display="inline"> consecutive sessions.</Typography>
        </Box>
      </Box>
    </Box>
  ) : null;
}

export function MigrationModalPCGContent({
  goal,
  migrationPreviewState,
}: IMigrationModalContentProps) {
  const currentCriteria = isPercentageMasteryCriteria(goal.mastery_criteria)
    ? goal.mastery_criteria
    : null;

  return migrationPreviewState ? (
    <Box py={2} data-testid="baselineVersionMigrationPCGContent">
      <Typography fontWeight={500}>
        This action may update the Baseline and Mastering criteria. Please review the data and make
        any necessary adjustment before concluding the treatment plan.
      </Typography>
      <Typography color={FORTA_ERROR} my={2} fontSize={14}>
        Calculations are based on saved data. If you have unsaved changes, please save them first.
      </Typography>
      <Box my={2}>
        <Typography fontSize={18} fontWeight={700}>
          Baseline
        </Typography>
        <Box p={2}>
          <Box>
            <Typography display="inline" fontWeight={600}>
              Current:{' '}
            </Typography>
            <Typography display="inline" fontWeight={700}>
              {goal?.baseline_at_creation ?? '__'}
            </Typography>
          </Box>
          <Box mt={0.5}>
            <Typography display="inline" fontWeight={600}>
              New:{' '}
            </Typography>
            <Typography display="inline" fontWeight={700}>
              {migrationPreviewState?.baseline_at_creation ?? '__'}
            </Typography>
            <Typography display="inline" mx={0.5}>
              out of
            </Typography>
            <Typography display="inline" fontWeight={700}>
              {migrationPreviewState?.measurement_units?.total_attempts ?? '__'}
            </Typography>
            <Typography display="inline" ml={0.5}>
              opportunities.
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box my={2}>
        <Typography fontSize={18} fontWeight={700} mb={2}>
          Mastering criteria
        </Typography>
        <Box px={2} fontWeight={500}>
          <Typography display="inline" fontWeight={600}>
            Current:{' '}
          </Typography>
          <Typography display="inline" fontWeight={700} ml={0.5}>
            {currentCriteria?.desired_amount ?? '__'}
          </Typography>
          <Typography display="inline"> out of </Typography>
          <Typography display="inline" fontWeight={700}>
            {currentCriteria?.total_attempts ?? '__'}
          </Typography>
          <Typography display="inline"> opportunities over </Typography>
          <Typography display="inline" fontWeight={700}>
            {currentCriteria?.practice_duration_count ?? '__'}
          </Typography>
          <Typography display="inline"> consecutive sessions.</Typography>
        </Box>
        <Box px={2} fontWeight={500} mt={0.5}>
          <Typography display="inline" fontWeight={600}>
            New:{' '}
          </Typography>
          <Typography display="inline">Client will correctly respond in</Typography>
          <Typography display="inline" fontWeight={700} ml={0.5}>
            {migrationPreviewState?.mastery_criteria?.desired_amount ?? '__'}
          </Typography>
          <Typography display="inline"> out of </Typography>
          <Typography display="inline" fontWeight={700}>
            {migrationPreviewState?.measurement_units?.total_attempts ?? '__'}
          </Typography>
          <Typography display="inline"> opportunities over </Typography>
          <Typography display="inline" fontWeight={700}>
            {migrationPreviewState?.mastery_criteria?.practice_duration_count ?? '__'}
          </Typography>
          <Typography display="inline"> consecutive sessions.</Typography>
        </Box>
      </Box>
    </Box>
  ) : null;
}
